import axios from "axios";
import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    AUTH_LOGIN_URL,
    AUTH_LOGOUT_URL,
    AUTH_REFRESH_URL,
    AUTH_LOGOUT_ALL_URL,
    AUTH_SESSIONS_URL,
    AUTH_REGISTER_URL,
    AUTH_CHANGE_PASSWORD_URL,
    AUTH_ME_URL,
    AUTH_SET_PIN_URL,
    AUTH_FORGOT_PASSWORD_URL,
    AUTH_DATUM_NAROZENI_URL,
    DEVICES_URL,
    DEVICE_BY_ID_URL,
} from "../url";
import { API_SERVER_URL } from "../../../resources/constants";
import type {
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    ChangePasswordRequest,
    Uzivatel,
    RefreshTokenResponse,
    ActiveSessionResponse,
    DeviceResponse,
    UpdateDeviceRequest,
} from "../generated";

const backendURL = API_SERVER_URL;

// Axios instance for login/refresh (without token interceptors to avoid circular dependencies)
export const authAxiosInstance = axios.create({
    baseURL: backendURL,
    withCredentials: true,
    timeout: 10000,
});

// -----------------------------
// AUTH METHODS
// -----------------------------

const login = (credentials: LoginRequest) => {
    return handleRequest<LoginResponse>(() =>
        authAxiosInstance.post(AUTH_LOGIN_URL, credentials)
    );
};

const logout = (refreshToken: string) => {
    return handleRequest(() =>
        authAxiosInstance.post(AUTH_LOGOUT_URL, { refreshToken })
    );
};

const refresh = (refreshToken: string) => {
    return handleRequest<RefreshTokenResponse>(() =>
        authAxiosInstance.post(AUTH_REFRESH_URL, { refreshToken })
    );
};

const logoutAll = () => {
    return handleRequest(() =>
        axiosInstance.post(AUTH_LOGOUT_ALL_URL)
    );
};

const getSessions = (refreshToken?: string) => {
    return handleRequest<ActiveSessionResponse[]>(() =>
        axiosInstance.post(AUTH_SESSIONS_URL, refreshToken ? { refreshToken } : undefined)
    );
};

// -----------------------------
// DEVICES
// -----------------------------

const getDevices = () => {
    return handleRequest<DeviceResponse[]>(() =>
        axiosInstance.get(DEVICES_URL)
    );
};

const updateDevice = (deviceId: number, data: UpdateDeviceRequest) => {
    return handleRequest<DeviceResponse>(() =>
        axiosInstance.put(DEVICE_BY_ID_URL(deviceId), data)
    );
};

const deleteDevice = (deviceId: number) => {
    return handleRequest(() =>
        axiosInstance.delete(DEVICE_BY_ID_URL(deviceId))
    );
};

const register = (data: RegisterRequest) => {
    return handleRequest<Uzivatel>(() =>
        axiosInstance.post(AUTH_REGISTER_URL, data)
    );
};

const changePassword = (data: ChangePasswordRequest) => {
    return handleRequest(() =>
        axiosInstance.post(AUTH_CHANGE_PASSWORD_URL, data)
    );
};

const getCurrentUser = () => {
    return handleRequest<Uzivatel>(() =>
        axiosInstance.get(AUTH_ME_URL)
    );
};

const setPin = (pin: string) => {
    return handleRequest(() =>
        axiosInstance.post(AUTH_SET_PIN_URL, { pin })
    );
};

const forgotPassword = (email: string) => {
    return handleRequest<{ message: string }>(() =>
        authAxiosInstance.post(AUTH_FORGOT_PASSWORD_URL, { email })
    );
};

// Self-service: uložení data narození (HO-22)
const setDatumNarozeni = (datumNarozeni: string) => {
    return handleRequest<{ success: boolean }>(() =>
        axiosInstance.put(AUTH_DATUM_NAROZENI_URL, { datumNarozeni })
    );
};

// -----------------------------

const AuthService = {
    login,
    logout,
    refresh,
    logoutAll,
    getSessions,
    getDevices,
    updateDevice,
    deleteDevice,
    register,
    changePassword,
    getCurrentUser,
    setPin,
    forgotPassword,
    setDatumNarozeni,
};

export default AuthService;
